import { ExposedPromise } from 'jakapa-utilities-lite';
import { Observable, Subject, Subscription } from 'rxjs';

export enum SelectKey {
	VIEW_ALL = 'viewAll',
}

/**
 * Select class to standardize selection components.
 */
export class Select<T> {
	private _options: Array<T> = [];
	private _value: T | null = null;
	private _loaded: ExposedPromise<boolean> = new ExposedPromise<boolean>();
	private _onChange: Subject<T | null> = new Subject<T | null>();
	private _onChangeOptions: Subject<Array<T>> = new Subject<Array<T>>();

	constructor(public readonly key: SelectKey | null = null) {}

	get options(): Array<T> {
		return this._options;
	}

	set options(options: Array<T>) {
		this._options = options;
		if (!this._options.includes(this._value)) {
			this.value = this._options[0] ?? null;
		}
		this._onChangeOptions.next(this._options);
	}

	get value(): T | null {
		return this._value;
	}

	set value(value: T | null) {
		this._value = value;
		this._onChange.next(this._value);
	}

	get loaded(): Promise<boolean> {
		return this._loaded.toPromise();
	}

	set loaded(loaded: boolean) {
		this._loaded.resolve(loaded);
	}

	/**
	 * Returns an observable that emits the value each time it changes.
	 * @returns {Observable<T | null>} An observable for new values.
	 */
	get onChange(): Observable<T | null> {
		return this._onChange.asObservable();
	}

	mirror(mirror: Select<T>): Subscription {
		const subscription = new Subscription();
		subscription.add(
			this._onChangeOptions.subscribe(() => (mirror.options = this._options)),
		);
		subscription.add(
			this._onChange.subscribe(() => (mirror.value = this._value)),
		);
		this.loaded.then(() => (mirror.loaded = true));
		return subscription;
	}
}
